import React, { useContext, useEffect, useState } from "react";
import { NavLink, Link } from "react-router-dom";
import { observer } from "mobx-react";
import {
	MdSettings,
	MdLogout,
	MdAdd,
	MdClose,
	MdKeyboardDoubleArrowLeft,
	MdKeyboardDoubleArrowRight,
} from "react-icons/md";
import { SideBarItems, SideBarGroups } from "./Data-items";
import { ContextStore } from "./../store/ContextStore";
import { AuthService } from "../services/AuthService";

const usersImageUrl = `${process.env.REACT_APP_API_URL}/api/images/users`;

// Navigation shared by the desktop sidebar and the mobile drawer.
// collapsed: icons-only mode; onToggleCollapse: shows the collapse button;
// onClose: shows a close button (drawer) and is called after navigating.
function SidebarNav({ collapsed = false, onToggleCollapse, onClose }) {
	const { authStore } = useContext(ContextStore);
	const user = authStore.getUser();
	const avatar = user?.image ? `${usersImageUrl}/${user.image}` : `${usersImageUrl}/profile_pic.jpg`;
	const [avatarFailed, setAvatarFailed] = useState(false);
	// try again if the user (and so the image) changes
	useEffect(() => setAvatarFailed(false), [avatar]);

	const handleLogOut = () => {
		new AuthService().logOut();
		window.location.assign("/admin/login");
	};

	return (
		<div className={`sn-nav${collapsed ? " is-collapsed" : ""}`}>
			<div className="sn-nav-top">
				{onClose && (
					<button className="sn-nav-close" onClick={onClose} aria-label="Close menu">
						<MdClose />
					</button>
				)}
				<Link
					to={user?._id ? `/admin/profile/${user._id}` : "/admin/dashboard"}
					className="sn-profile"
					title={collapsed ? user?.username : "View profile"}
					onClick={onClose}
				>
					{avatarFailed ? (
						<span className="sn-avatar sn-avatar--initial" aria-hidden="true">
							{(user?.username || "A").charAt(0).toUpperCase()}
						</span>
					) : (
						<img src={avatar} alt="" className="sn-avatar" onError={() => setAvatarFailed(true)} />
					)}
					<span className="sn-profile-text">
						<span className="sn-profile-name">{user?.username || "Admin"}</span>
						{user?.role && <span className="sn-role">{user.role}</span>}
					</span>
				</Link>
			</div>

			<nav className="sn-nav-links" aria-label="Admin navigation">
				{SideBarGroups.map((group) => (
					<div key={group} className="sn-group">
						<span className="sn-group-title">{group}</span>
						<ul>
							{SideBarItems.filter((item) => item.group === group).map((item) => (
								<li key={item.name} className="sn-item">
									<NavLink
										to={item.href}
										className={({ isActive }) => `sn-link${isActive ? " is-active" : ""}`}
										title={collapsed ? item.name : undefined}
										onClick={onClose}
									>
										<span className="sn-link-icon">{item.icon}</span>
										<span className="sn-link-text">{item.name}</span>
									</NavLink>
									{item.add && (
										<Link
											to={item.add.href}
											className="sn-add"
											title={item.add.name}
											aria-label={item.add.name}
											onClick={onClose}
										>
											<MdAdd />
										</Link>
									)}
								</li>
							))}
						</ul>
					</div>
				))}
			</nav>

			<div className="sn-nav-bottom">
				<NavLink
					to="settings"
					className={({ isActive }) => `sn-link${isActive ? " is-active" : ""}`}
					title={collapsed ? "Settings" : undefined}
					onClick={onClose}
				>
					<span className="sn-link-icon">
						<MdSettings />
					</span>
					<span className="sn-link-text">Settings</span>
				</NavLink>
				<button className="sn-link sn-logout" onClick={handleLogOut} title={collapsed ? "Logout" : undefined}>
					<span className="sn-link-icon">
						<MdLogout />
					</span>
					<span className="sn-link-text">Logout</span>
				</button>
				{onToggleCollapse && (
					<button
						className="sn-collapse"
						onClick={onToggleCollapse}
						aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
						title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
					>
						{collapsed ? <MdKeyboardDoubleArrowRight /> : <MdKeyboardDoubleArrowLeft />}
						<span className="sn-link-text">Collapse</span>
					</button>
				)}
			</div>
		</div>
	);
}

export default observer(SidebarNav);
